import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { HERO } from "@/constants/testIds";

// Kinetic hero with masked line-by-line reveal + parallax product moment.
const heroImage =
  "https://images.unsplash.com/photo-1598218940656-7126545fd283?auto=format&fit=crop&w=1600&q=80";

const MaskLine = ({ children, delay = 0 }) => (
  <span className="mask-line">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.2, delay, ease: [0.16, 1, 0.3, 1] }}
      className="inline-block will-change-transform"
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid={HERO.section}
      className="relative min-h-[100svh] w-full overflow-hidden pt-28 md:pt-36"
    >
      {/* Meta overline */}
      <div className="px-6 md:px-10 flex items-center justify-between">
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.8 }}
          className="text-[10px] tracking-[0.32em] uppercase font-sans text-[#1C1C1A]/70"
        >
          Est. — Family Atelier · 25+ Years of Experience
        </motion.span>
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.8 }}
          className="text-[10px] tracking-[0.32em] uppercase font-sans text-[#1C1C1A]/70"
        >
          Ch. 01 — Prologue
        </motion.span>
      </div>

      {/* Main headline stack */}
      <motion.div
        style={{ y: textY }}
        className="relative z-10 mt-10 md:mt-16 px-6 md:px-10"
        data-testid={HERO.headline}
      >
        <h1 className="font-serif font-light leading-[0.9] tracking-[-0.02em] text-[16vw] md:text-[13vw] lg:text-[11.5vw]">
          <MaskLine delay={0.2}>Diamant</MaskLine>
          <MaskLine delay={0.35}>
            <span className="italic text-[#9A8B73]">Jewellers</span>
          </MaskLine>
        </h1>
      </motion.div>

      {/* Floating parallax hero image */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute right-4 md:right-10 top-[38%] md:top-[26%] w-[52%] md:w-[36%] max-w-[560px] aspect-[3/4] overflow-hidden z-0"
      >
        <motion.img
          initial={{ scale: 1.25, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          src={heroImage}
          alt="Woman wearing DIAMANT necklace"
          className="w-full h-full object-cover"
        />
        <motion.div
          initial={{ scaleY: 1 }}
          animate={{ scaleY: 0 }}
          transition={{ duration: 1.2, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0 bg-[#F7F5F2] origin-top"
        />
      </motion.div>

      {/* Bottom row */}
      <motion.div
        style={{ y: textY }}
        className="absolute bottom-0 left-0 right-0 z-10 px-6 md:px-10 pb-10"
      >
        <div className="grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-5">
            <MaskLine delay={0.9}>
              <p className="font-serif text-xl md:text-2xl leading-snug max-w-md">
                You dream, we create — heirlooms designed with love, soul and
                style for your best moments.
              </p>
            </MaskLine>
          </div>

          <div className="hidden md:block col-span-3" />

          <div className="col-span-12 md:col-span-4 flex flex-col md:items-end gap-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.6, duration: 0.8 }}
              className="flex items-center gap-4"
            >
              <a
                href="#collections"
                data-testid={HERO.ctaExplore}
                className="group inline-flex items-center gap-3 text-xs uppercase tracking-[0.24em] font-sans border border-[#1C1C1A] px-6 py-4 hover:bg-[#1C1C1A] hover:text-[#F7F5F2] transition-colors duration-500"
              >
                Explore Collections
                <span
                  aria-hidden="true"
                  className="inline-block transition-transform duration-500 group-hover:translate-x-1"
                >
                  →
                </span>
              </a>
              <a
                href="#atelier"
                data-testid={HERO.ctaAtelier}
                className="link-underline text-xs uppercase tracking-[0.24em] font-sans"
              >
                Enter the Atelier
              </a>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 0.8 }}
          data-testid={HERO.scrollHint}
          className="mt-10 flex items-center gap-3 text-[10px] uppercase tracking-[0.32em] font-sans text-[#1C1C1A]/60"
        >
          <span className="blink">◆</span>
          Scroll — Chapter Two
        </motion.div>
      </motion.div>
    </section>
  );
}
